import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import { clock } from '@/services/clock';

import type { DashboardEditState } from '../dashboardEdit.types';
import { generateUniqueTabName } from '../lib/dashboardEditValidation';
import { reorderArray } from '../lib/reorder';

type State = DashboardEditState;

/** Touches the current tab (or the first tab as a fallback) to bump the dashboard-level updatedAt rollup. */
const touchCurrentOrFirstTab = (state: State): void => {
  const tab = state.draftTabs?.find(t => t.id === state.currentTabId) ?? state.draftTabs?.[0];
  if (tab) tab.updatedAt = clock.date().toISOString();
};

const setCurrentTab: CaseReducer<State, PayloadAction<{ tabId: string }>> = (state, action) => {
  if (state.draftTabs.some(t => t.id === action.payload.tabId)) {
    state.currentTabId = action.payload.tabId;
  }
};

const addTab: CaseReducer<State, PayloadAction<{ tabId: string; name: string }>> = (
  state,
  action
) => {
  const { tabId, name } = action.payload;
  const uniqueName = generateUniqueTabName(state.draftTabs, name);
  state.draftTabs.push({
    id: tabId,
    name: uniqueName,
    sourceName: null,
    updatedAt: clock.date().toISOString()
  });
  state.tabDrafts[tabId] = { marketTabApps: [], tiles: [] };
  state.isDirty = true;
};

const renameTab: CaseReducer<State, PayloadAction<{ tabId: string; name: string }>> = (
  state,
  action
) => {
  const tab = state.draftTabs.find(t => t.id === action.payload.tabId);
  if (tab) {
    tab.name = action.payload.name;
    tab.updatedAt = clock.date().toISOString();
    state.isDirty = true;
  }
};

const deleteTab: CaseReducer<State, PayloadAction<{ tabId: string }>> = (state, action) => {
  const { tabId } = action.payload;
  if (state.draftTabs.length <= 1) return;

  const tabIndex = state.draftTabs.findIndex(t => t.id === tabId);
  if (tabIndex === -1) return;

  state.draftTabs = state.draftTabs.filter(t => t.id !== tabId);
  delete state.tabDrafts[tabId];

  if (state.currentTabId === tabId) {
    state.currentTabId = state.draftTabs[Math.max(0, tabIndex - 1)]?.id ?? null;
  }

  touchCurrentOrFirstTab(state);
  state.isDirty = true;
};

const reorderTabs: CaseReducer<State, PayloadAction<{ fromIndex: number; toIndex: number }>> = (
  state,
  action
) => {
  state.draftTabs = reorderArray(state.draftTabs, action.payload.fromIndex, action.payload.toIndex);
  touchCurrentOrFirstTab(state);
  state.isDirty = true;
};

export const tabReducers = { setCurrentTab, addTab, renameTab, deleteTab, reorderTabs };
